/**
 * Typed wrapper around the bridge exposed by `apps/desktop/preload.cjs`.
 * Centralises feature detection so callers don't have to keep checking
 * `window.aicavite?.isElectron`.
 */

interface AicaviteBridge {
  isElectron?: boolean;
  platform?: string;
  versions?: { node?: string; chrome?: string; electron?: string };
  /** Show an OS-level notification via Electron's main process. */
  showNativeNotification?: (
    title: string,
    body: string,
    data?: { link?: string }
  ) => Promise<void> | void;
  /** Subscribe to clicks on Electron-shown notifications. */
  onNotificationClick?: (handler: (link: string) => void) => () => void;
}

// Window.aicavite is declared in src/lib/runtime.ts with an open shape so we
// don't have to re-declare it here.

function bridge(): AicaviteBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return window.aicavite as AicaviteBridge | undefined;
}

export function isElectron(): boolean {
  return typeof window !== "undefined" && window.aicavite?.isElectron === true;
}

export async function showElectronNotification(
  title: string,
  body: string,
  data?: { link?: string }
) {
  if (!isElectron() || !window.aicavite?.showNativeNotification) return;
  try {
    await window.aicavite.showNativeNotification(title, body, data);
  } catch (e) {
    console.warn("[electron-bridge] showNativeNotification failed:", e);
  }
}

export function onElectronNotificationClick(handler: (link: string) => void): () => void {
  if (!isElectron() || !window.aicavite?.onNotificationClick) return () => {};
  return window.aicavite.onNotificationClick(handler);
}
